import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface DropdownItem {
  value: any;
  label: string;
  longLabel?: boolean;
  badges?: { label: string; color: string }[];
  hideBadgeOnSelection?: boolean;
  icons?: {
    iconName?: string;
    iconSrc?: string;
    iconTooltip?: string;
  }[];
  [key: string]: any;
}

@Component({
  selector: 'searchable-dropdown',
  templateUrl: './searchable-dropdown.component.html',
  styleUrls: ['./searchable-dropdown.component.scss'],
})
export class SearchableDropdownComponent implements OnInit, OnDestroy, OnChanges {
  items$ = new BehaviorSubject<DropdownItem[]>([]);
  searchText = new FormControl('');
  longItemLabelDetermined: boolean = false;
  _onDestroy = new Subject<void>();

  /** Label of the dropdown placeholder */
  @Input() placeholderLabel: string;

  /** Label of the search placeholder */
  @Input() searchLabel: string;

  /** Label to be shown when no entries are found. Set to null if no message should be shown. */
  @Input() noEntriesFoundLabel: string;

  /** An array of dropdown items */
  @Input() items: DropdownItem[];

  /** Initial selected value */
  @Input() selection: DropdownItem;

  /** FormControl to be used for the MatSelect element */
  @Input('control') formControl: FormControl;

  /** Emits when the MatSelect panel has been toggled */
  @Output() openedChange = new EventEmitter<boolean>();

  @ViewChild('matSelect') matSelect: MatSelect;

  constructor() { }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngOnInit() {
    // listen for search field value changes
    this.searchText.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterItems();
    });
  }

  ngAfterViewInit() {
    this.formControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((value) => {
      this.setNewSelection(value);
    });

    this.matSelect.openedChange.pipe(takeUntil(this._onDestroy)).subscribe((opened: boolean) => {
      this.openedChange.emit(opened);
      if (opened) {
        this.filterItems();
      }
      this.determineLongLabels();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.items) {
      this.longItemLabelDetermined = false;
      this.items$.next(changes.items.currentValue);
      if (!this.selection && this.formControl.getRawValue()) {
        this.setNewSelection(this.formControl.getRawValue());
      }
    }
  }

  setNewSelection(value: any) {
    let newSelection = this.items?.find((item) => item.value == value);
    if (newSelection) {
      this.selection = newSelection;
      this.filterItems();
      this.determineLongLabelForSelected();
    }
  }

  filterItems(): void {
    this.items$.next(this.items);

    if (this.searchText && this.searchText.value) {
      let search = this.searchText.value?.trim().toLowerCase();
      if (search && !!this.items) {
        this.items$.next(this.items.filter((item) => item?.label?.toLocaleLowerCase().indexOf(search) !== -1));
      }
    }
  }

  /* istanbul ignore next */
  determineLongLabelForSelected() {
    if (this.selection) {
      setTimeout(() => {
        document.querySelectorAll('.selected-item-name').forEach((el) => {
          if (el.scrollWidth > (el as HTMLElement).offsetWidth) {
            this.selection!.longLabel = true;
          }
        });
      }, 100);
    }
  }

  /* istanbul ignore next */
  determineLongLabels() {
    if (!this.longItemLabelDetermined) {
      document.querySelectorAll('.item-name').forEach((el, index) => {
        if (el.scrollWidth > (el as HTMLElement).offsetWidth) {
          this.items[index].longLabel = true;
        }
      });
      this.items$.next(this.items);
      this.longItemLabelDetermined = true;
    }
  }
}
